import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightOutlined, InstagramOutlined } from "@ant-design/icons";

import "./regina-story.component.styles.scss";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE, conversions } from "../../../lib/analytics";
import { useLang } from "../../../lib/i18n";
import { useReveal } from "../../../lib/motion";

interface ReginaStoryProps {
  portraitUrl?: string;
}

const ReginaStory: React.FC<ReginaStoryProps> = ({
  portraitUrl = "/heroes/massaggio-emozionale.jpg",
}) => {
  const { lang, lp } = useLang();
  const isEn = lang === "en";
  const portraitRef = useReveal<HTMLDivElement>();
  const bodyRef = useReveal<HTMLDivElement>();
  return (
    <section className="lvs-regina" aria-labelledby="regina-title">
      <div className="lvs-regina__inner lvs-container">
        <div className="lvs-regina__portrait lvs-reveal" ref={portraitRef}>
          <div
            className="lvs-regina__image"
            style={{ backgroundImage: `url(${portraitUrl})` }}
            aria-hidden="true"
          />
          <img
            src={portraitUrl}
            alt={
              isEn
                ? "Regina, founder and wellness therapist at Laviens — Wellness Studio in Sesto Fiorentino, Florence"
                : "Regina, fondatrice e terapista olistica di Laviens — Studio Olistico a Sesto Fiorentino, Firenze"
            }
            loading="lazy"
            decoding="async"
            className="lvs-regina__imageSr"
            itemProp="image"
          />
          <span className="lvs-regina__caption">
            {isEn
              ? "Regina, founder of Laviens"
              : "Regina, fondatrice di Laviens"}
          </span>
        </div>

        <div className="lvs-regina__body lvs-reveal-group" ref={bodyRef}>
          <span className="eyebrow lvs-regina__eye">
            {isEn ? "Who welcomes you" : "Chi ti accoglie"}
          </span>
          <h2 id="regina-title" className="lvs-regina__title">
            <em>Regina.</em>
            <br />
            {isEn
              ? "Hands that listen, before they touch."
              : "Mani che ascoltano, prima di toccare."}
          </h2>
          <p className="lvs-regina__lead">
            {isEn
              ? '"Every body has a story. My job is to slow down enough to recognize it — and let the breath do the rest."'
              : '"Ogni corpo ha una storia. Il mio lavoro è rallentare abbastanza da riconoscerla — e lasciare che il respiro torni a fare il resto."'}
          </p>
          <ul className="lvs-regina__facts">
            <li>
              <span className="lvs-regina__factNum">10+</span>
              <span className="lvs-regina__factLbl">
                {isEn
                  ? "years of holistic practice"
                  : "anni di pratica olistica"}
              </span>
            </li>
            <li>
              <span className="lvs-regina__factNum">6</span>
              <span className="lvs-regina__factLbl">
                {isEn ? "techniques mastered" : "tecniche padroneggiate"}
              </span>
            </li>
            <li>
              <span className="lvs-regina__factNum">1:1</span>
              <span className="lvs-regina__factLbl">
                {isEn
                  ? "private studio, never rushed"
                  : "studio privato, mai di corsa"}
              </span>
            </li>
          </ul>
          <div className="lvs-regina__actions">
            <Link
              to={lp("/booking")}
              className="lvs-regina__cta"
              onClick={() => conversions.book("regina_story")}
            >
              {isEn ? "Book with Regina" : "Prenota con Regina"}
              <ArrowRightOutlined />
            </Link>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="lvs-regina__ig"
              onClick={() => conversions.instagram("regina_story")}
            >
              <InstagramOutlined />
              <span>{INSTAGRAM_HANDLE}</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReginaStory;
